import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  Calendar,
  Users,
  Eye,
  X,
  Clock,
  CalendarDays,
  CheckCircle2,
  Zap,
} from "lucide-react";

import {
  useCreateExecutiveSetMutation,
  useGetExecutiveSetsQuery,
  useActivateExecutiveSetMutation,
} from "../api/executiveSetApi";

import { PERMS } from "../../../utils/perms";
import AccessGate from "../../../components/AccessGate";
import PageGate from "../../../components/PageGate";
import { useAccess } from "../../../utils/useAccess";

type CreateForm = {
  name: string;
  start_date: string;
  end_date: string;
};

// --- Sub-components ---
function StatCard({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: string | number;
  icon: any;
}) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm flex items-center justify-between">
      <div>
        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          {title}
        </div>
        <div className="text-2xl font-black text-gray-900 mt-1">{value}</div>
      </div>
      <div className="p-2 bg-gray-50 rounded-lg text-gray-400">
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}

function toMsg(err: any): string {
  return (
    err?.data?.message || err?.error || err?.message || "Something went wrong"
  );
}

function validate(form: CreateForm) {
  const errors: Partial<Record<keyof CreateForm, string>> = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.start_date) errors.start_date = "Start date is required";
  if (!form.end_date) errors.end_date = "End date is required";
  if (form.start_date && form.end_date && form.start_date > form.end_date) {
    errors.end_date = "End date must be after (or same as) start date";
  }
  return errors;
}

export default function ExecutiveSetsPage() {
  const { user } = useAccess();
  const { data = [], isLoading, error, refetch } = useGetExecutiveSetsQuery();
  const [createSet, { isLoading: creating }] = useCreateExecutiveSetMutation();
  const [activateSet, { isLoading: activating }] = useActivateExecutiveSetMutation();

  const [optimistic, setOptimistic] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [form, setForm] = useState<CreateForm>({
    name: "",
    start_date: "",
    end_date: "",
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateForm, string>>
  >({});

  const nameRef = useRef<HTMLInputElement | null>(null);

  const list = useMemo(() => {
    const merged = [...optimistic, ...data];
    return merged.sort((a: any, b: any) => {
      const da = a.start_date ? new Date(a.start_date).getTime() : 0;
      const db = b.start_date ? new Date(b.start_date).getTime() : 0;
      return db - da;
    });
  }, [data, optimistic]);

  const closeModal = () => {
    setOpen(false);
    setForm({ name: "", start_date: "", end_date: "" });
    setErrors({});
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) return;

    const tempId = `temp-${Date.now()}`;
    const optimisticItem = {
      id: tempId,
      name: form.name,
      start_date: form.start_date,
      end_date: form.end_date,
      executives: [],
      __optimistic: true,
    };

    setOptimistic((prev) => [optimisticItem, ...prev]);

    try {
      const res: any = await createSet(form).unwrap();
      setOptimistic((prev) => prev.filter((x) => x.id !== tempId));
      setMsg({
        type: "success",
        text: res?.message || "Executive set created successfully",
      });
      closeModal();
    } catch (err: any) {
      setOptimistic((prev) => prev.filter((x) => x.id !== tempId));
      setMsg({ type: "error", text: toMsg(err) });
    }
  };

  const handleActivate = async (id: number, name: string) => {
    if (!window.confirm(`Set "${name}" as the active executive term? All other terms will be marked inactive.`)) return;
    try {
      await activateSet(id).unwrap();
      setMsg({ type: "success", text: `"${name}" is now the active executive term.` });
    } catch (err: any) {
      setMsg({ type: "error", text: toMsg(err) });
    }
  };

  return (
    <PageGate perm={PERMS.executive_set?.access}>
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-900 border border-gray-800">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-black text-gray-900 tracking-tight text-xl">
                  Executive Terms
                </h1>
                <p className="text-sm text-gray-500">
                  Logged in as{" "}
                  <span className="font-semibold">{user?.email}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-medium transition-colors"
                disabled={isLoading}
              >
                <RefreshCw
                  className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              <AccessGate perm={PERMS.executive_set?.create}>
                <button
                  onClick={() => {
                    setMsg(null);
                    setOpen(true);
                    setTimeout(() => nameRef.current?.focus(), 50);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm font-medium transition-transform active:scale-95 shadow-lg shadow-gray-900/20"
                >
                  <Plus className="w-4 h-4" />
                  New Set
                </button>
              </AccessGate>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Terms" value={data.length} icon={Clock} />
          <StatCard
            title="Total Executives"
            value={data.reduce(
              (acc, curr) => acc + (curr.executives?.length || 0),
              0
            )}
            icon={Users}
          />
          <StatCard
            title="Active Term"
            value={data.filter((s) => s.is_active).length}
            icon={CheckCircle2}
          />
        </div>

        {/* Feedback Banner */}
        {msg && (
          <div
            className={`p-4 rounded-xl border flex justify-between items-center animate-in fade-in slide-in-from-top-2 ${
              msg.type === "success"
                ? "bg-green-50 border-green-100 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <span className="text-sm font-medium">{msg.text}</span>
            <button onClick={() => setMsg(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Table Section */}
        <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
          {isLoading ? (
            <div className="py-20 text-center flex flex-col items-center gap-2">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-200" />
              <span className="text-gray-400 text-sm">
                Loading Executive registry...
              </span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b bg-gray-50/50 text-[11px] font-black uppercase tracking-widest text-gray-400">
                    <th className="py-4 px-6">Term Name</th>
                    <th className="py-4 px-6">Start Date</th>
                    <th className="py-4 px-6">End Date</th>
                    <th className="py-4 px-6">Executives</th>
                    <th className="py-4 px-6">Status</th>
                    <AccessGate perm={PERMS.executive_set?.detailsAccess}>
                      <th className="py-4 px-6 text-right">Actions</th>
                    </AccessGate>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {list.map((s: any) => (
                    <tr
                      key={s.id}
                      className={`transition-colors group ${
                        s.is_active ? "bg-emerald-50/40" : "hover:bg-gray-50/80"
                      }`}
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">
                            {s.name}
                          </span>
                          {s.__optimistic && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 animate-pulse font-bold uppercase">
                              Syncing...
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-gray-600">
                          <CalendarDays className="w-3.5 h-3.5 text-gray-300" />
                          {s.start_date}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-3.5 h-3.5 text-gray-300" />
                          {s.end_date}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-bold">
                          <Users className="w-3 h-3" />
                          {(s.executives || []).length}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {s.is_active ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-black uppercase tracking-wider">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-400 text-[11px] font-black uppercase tracking-wider">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <AccessGate perm={PERMS.executive_set?.detailsAccess}>
                        <td className="py-4 px-6 text-right">
                          {String(s.id).startsWith("temp-") ? (
                            <span className="text-xs text-gray-300 font-mono">
                              PENDING
                            </span>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              {!s.is_active && (
                                <AccessGate perm={PERMS.executive_set?.update}>
                                  <button
                                    onClick={() => handleActivate(s.id, s.name)}
                                    disabled={activating}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-200 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold transition-all"
                                    title="Set as the active executive term"
                                  >
                                    <Zap className="w-3 h-3" />
                                    Set Active
                                  </button>
                                </AccessGate>
                              )}
                              <Link
                                to={`/executive-sets/${s.id}`}
                                className="inline-flex items-center gap-2 px-3 py-1.5 border rounded-lg bg-white hover:bg-gray-50 text-gray-600 text-xs font-bold transition-all shadow-sm"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                DETAILS
                              </Link>
                            </div>
                          )}
                        </td>
                      </AccessGate>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal */}
        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border p-6 animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-black text-gray-900 tracking-tight">
                    New Executive Term
                  </h2>
                </div>
                <button
                  onClick={closeModal}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <p className="text-sm text-gray-500 mt-2">
                Create a new timeframe for a set of executive officers.
              </p>

              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
                    Term Name
                  </label>
                  <input
                    ref={nameRef}
                    value={form.name}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, name: e.target.value }));
                      if (errors.name)
                        setErrors((p) => ({ ...p, name: undefined }));
                    }}
                    placeholder="e.g. 2025/2026 Academic Session"
                    className={`w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all ${
                      errors.name
                        ? "border-red-300 ring-red-50"
                        : "border-gray-200"
                    }`}
                    disabled={creating}
                  />
                  {errors.name && (
                    <div className="text-xs text-red-600 mt-1 font-medium">
                      {errors.name}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={form.start_date}
                      onChange={(e) =>
                        setForm({ ...form, start_date: e.target.value })
                      }
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all"
                    />
                    {errors.start_date && (
                      <div className="text-xs text-red-600 mt-1 font-medium">
                        {errors.start_date}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={form.end_date}
                      onChange={(e) =>
                        setForm({ ...form, end_date: e.target.value })
                      }
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all"
                    />
                    {errors.end_date && (
                      <div className="text-xs text-red-600 mt-1 font-medium">
                        {errors.end_date}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-3 rounded-xl border font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-900/20 flex items-center justify-center gap-2"
                  >
                    {creating ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {creating ? "Syncing..." : "Create Term"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PageGate>
  );
}
